import { useEffect, useState } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

/**
 * La barra de consulta del tablero: rango de fechas, vendedor, campaña,
 * grupo y sucursal.
 *
 * ABRE EN EL MES ACTUAL, SIEMPRE
 * ==============================
 *
 * Es lo que el dueño pidió y tiene una razón práctica: un tablero que abre
 * vacío obliga a configurar antes de ver nada, y quien entra de paso no lo
 * hace. Abriendo en el mes en curso, la primera pantalla ya dice algo.
 *
 * CADA GIRO LLAMA A SU SUCURSAL COMO LE TOCA
 * ==========================================
 *
 * "Sucursal" en un salón, "Sede" en una escuela, "Desarrollo" en una
 * inmobiliaria. El nombre sale del diccionario del giro, no de un texto fijo:
 * escribirle "Sucursal" a un desarrollador inmobiliario es hablarle en un
 * idioma que no es el suyo, y eso se nota más de lo que parece.
 *
 * LO QUE NO TIENE, NO SE ENSEÑA
 * =============================
 *
 * Un negocio de una sola sucursal no ve el filtro de sucursal, y uno sin
 * grupos de reparto no ve el de grupo. Un selector con una sola opción no es
 * un filtro: es un estorbo que hace la pantalla más difícil de leer.
 */
export default function FiltrosDelTablero({ filtros, onCambio }) {
  // Las etiquetas van por el traductor, no en español fijo: la plataforma ya
  // habla cuatro idiomas y una barra en español dentro de una pantalla en
  // portugués se lee como un descuido. El nombre de la sucursal es la
  // excepción y viene del diccionario del giro, porque ahí manda el negocio.
  const { t } = useLocale();
  const [equipo, setEquipo] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [nombreSucursal, setNombreSucursal] = useState('Sucursal');

  useEffect(() => {
    // Cada una por su lado: si una falla, las demás siguen sirviendo. Un
    // filtro de menos molesta; la barra entera en blanco deja sin tablero.
    // /tenant/users NO existe (daba 404 silencioso y dejaba el filtro de equipo
    // vacío); el equipo del negocio vive en /tenant/business/team.
    api.get('/tenant/business/team').then(r => setEquipo(r.data?.items || r.data || [])).catch(() => {});
    api.get('/tenant/assignment/pools').then(r => setGrupos(r.data?.items || r.data || [])).catch(() => {});
    api.get('/tenant/locations').then(r => setSucursales(r.data?.items || r.data || [])).catch(() => {});
    api.get('/tenant/business/dictionary')
      .then(r => setNombreSucursal(r.data?.location_label || 'Sucursal'))
      .catch(() => {});
  }, []);

  const set = (clave, valor) => onCambio({ ...filtros, [clave]: valor });
  const hayFiltro = filtros.vendedor || filtros.campana || filtros.grupo || filtros.sucursal;

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="filtros-del-tablero">
      <div className="w-36">
        <label className="text-[10px] text-[#64748B] block mb-0.5">{t('filtros.from', {}) || 'Desde'}</label>
        <Input type="date" className="h-8 text-xs" value={filtros.desde || ''}
          onChange={e => set('desde', e.target.value)} data-testid="filtro-desde" />
      </div>
      <div className="w-36">
        <label className="text-[10px] text-[#64748B] block mb-0.5">{t('filtros.to', {}) || 'Hasta'}</label>
        <Input type="date" className="h-8 text-xs" value={filtros.hasta || ''}
          onChange={e => set('hasta', e.target.value)} data-testid="filtro-hasta" />
      </div>

      {equipo.length > 1 && (
        <div className="w-40">
          <label className="text-[10px] text-[#64748B] block mb-0.5">{t('filtros.seller', {}) || 'Vendedor'}</label>
          <Select value={filtros.vendedor || 'todos'} onValueChange={v => set('vendedor', v === 'todos' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="filtro-vendedor"><SelectValue placeholder={t('common.all', {}) || 'Todos'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t('common.all', {}) || 'Todos'}</SelectItem>
              {equipo.map(m => <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {grupos.length > 0 && (
        <div className="w-36">
          <label className="text-[10px] text-[#64748B] block mb-0.5">{t('filtros.group', {}) || 'Grupo'}</label>
          <Select value={filtros.grupo || 'todos'} onValueChange={v => set('grupo', v === 'todos' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="filtro-grupo"><SelectValue placeholder={t('common.all', {}) || 'Todos'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t('common.all', {}) || 'Todos'}</SelectItem>
              {grupos.map(g => <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {sucursales.length > 1 && (
        <div className="w-40">
          <label className="text-[10px] text-[#64748B] block mb-0.5">{nombreSucursal}</label>
          <Select value={filtros.sucursal || 'todas'} onValueChange={v => set('sucursal', v === 'todas' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="filtro-sucursal"><SelectValue placeholder={t('common.all_f', {}) || 'Todas'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">{t('common.all_f', {}) || 'Todas'}</SelectItem>
              {sucursales.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="w-40">
        <label className="text-[10px] text-[#64748B] block mb-0.5">{t('filtros.campaign', {}) || 'Campaña'}</label>
        <Input value={filtros.campana || ''} onChange={e => set('campana', e.target.value)}
          placeholder="Ej. Promo Verano" className="h-8 text-xs" data-testid="filtro-campana" />
      </div>

      {hayFiltro && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-[#64748B]"
          onClick={() => onCambio({ desde: filtros.desde, hasta: filtros.hasta })}
          data-testid="filtro-limpiar">
          {t('filtros.clear', {}) || 'Quitar filtros'}
        </Button>
      )}
    </div>
  );
}

/** El primer y el último día del mes en curso, como los espera un input date. */
export function mesActual() {
  const h = new Date();
  const dos = n => String(n).padStart(2, '0');
  return {
    desde: `${h.getFullYear()}-${dos(h.getMonth() + 1)}-01`,
    hasta: `${h.getFullYear()}-${dos(h.getMonth() + 1)}-${dos(h.getDate())}`,
  };
}
